import type { DbResult } from '@/lib/db-tenant'

/**
 * Page size for exhaustive reads (see {@link fetchAllRows}).
 *
 * Matches PostgREST's `db-max-rows` on our projects (`supabase/config.toml:9`;
 * hosted projects inherit the same 1000 default from the dashboard). Asking for
 * a larger window cannot return more rows — the server caps the response either
 * way — so this is the largest page that still costs a single round trip.
 */
const EXHAUSTIVE_PAGE_SIZE = 1000

/**
 * Runaway backstops for {@link fetchAllRows}. NOT business rules.
 *
 * The walk's only natural terminator is an empty page, which trusts the server
 * to eventually send one. A server that keeps answering non-empty pages — a
 * proxy replaying a cached page, a filter silently dropped upstream, a mock that
 * ignores `.range()` — would otherwise spin the tab forever. Both ceilings sit
 * far above any realistic population of the tables these reads cover (bank
 * accounts, Sage accounts, facilities, one day's balances, one Cash Position
 * cell's projections).
 *
 * The request ceiling is separate from the row ceiling because a server capping
 * pages far BELOW {@link EXHAUSTIVE_PAGE_SIZE} needs proportionally more
 * requests to deliver the same rows, and that case must be caught too.
 */
const EXHAUSTIVE_MAX_ROWS = 50_000
const EXHAUSTIVE_MAX_PAGES = 200

/**
 * Reads EVERY row a query matches, one page at a time.
 *
 * PostgREST caps each response at `db-max-rows` (1000 here) and does it
 * SILENTLY: the truncated response carries no error, only HTTP 206 and a
 * `Content-Range` header that `postgrest-js` does not surface as a failure. A
 * truncated read is therefore indistinguishable from a complete one at the call
 * site. That is not hypothetical: this exact truncation shipped as the live
 * DCP-333 defect.
 *
 * The reads wired through this helper are the ones whose result must be
 * COMPLETE — a denominator, a coverage count, an "is anything missing?" check,
 * the full list behind a report figure — where a truncation turns into
 * confident nonsense the moment the table outgrows one page. Those reads must
 * page. That is deliberately NOT a rule for every read: sibling reads in
 * `db-tenant` still take the single-page risk on
 * purpose, because paging them would feed an unbounded id list into an
 * un-chunked `.in()` filter and trade a silent truncation for a hard
 * URL-length failure (see the residual note in `bankBalance.getCoverage`).
 *
 * Three things make the loop safe, and each is easy to get wrong:
 *
 *   - It advances by the number of rows actually RETURNED and stops only on an
 *     EMPTY page, rather than stopping as soon as a page comes back shorter than
 *     it asked for. The short-page test looks equivalent and is not: if
 *     `db-max-rows` is ever configured BELOW {@link EXHAUSTIVE_PAGE_SIZE}, every
 *     page is short, and the very cap this helper exists to defeat would be read
 *     as "last page" — reintroducing the bug in the code written to prevent it.
 *     The price is one extra request per call that comes back empty; paid
 *     deliberately, because the alternative failure is silent.
 *   - The caller MUST end `page`'s sort with a stable, UNIQUE `.order(...)` key.
 *     Postgres guarantees no ordering without ORDER BY, and a non-total order
 *     lets the server arrange tied rows differently per request, so an offset
 *     walk can hand back one row twice and never hand back another.
 *   - It returns an ERROR when a backstop trips instead of returning what it has
 *     so far: a partial population silently mislabelled complete is the original
 *     defect, so the caller must be able to tell the two apart. That loudness
 *     stops at the return value — the warning reads in `db-tenant` log the
 *     error and degrade to showing no warning, while the "Manage projections"
 *     modal shows it inline as its load error.
 *
 * @param page - issues one page of the query over the inclusive row range
 *   [from, to]; must apply the same filters and total order on every call
 * @returns every matching row, or the first error encountered
 */
export async function fetchAllRows<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: { message: string; code: string } | null }>,
): Promise<DbResult<T[]>> {
  const all: T[] = []
  let from = 0
  let pages = 0

  for (;;) {
    const { data, error } = await page(from, from + EXHAUSTIVE_PAGE_SIZE - 1)
    if (error) {
      return { data: null, error: { message: error.message, code: error.code } }
    }

    const rows = data ?? []
    if (rows.length === 0) {
      return { data: all, error: null }
    }

    all.push(...rows)
    // Advance by what the server ACTUALLY sent, never by EXHAUSTIVE_PAGE_SIZE:
    // against a lower server cap the latter would step straight over the rows it
    // declined to send.
    from += rows.length
    pages += 1

    // The walk is cut off BEFORE the request that would have proved the table
    // exhausted, so completeness is genuinely unknown here — a table holding
    // exactly EXHAUSTIVE_MAX_ROWS rows trips this on its last FULL page even
    // though nothing was missed. The message says only what is knowable
    // ("cannot be treated as complete"); claiming the result IS incomplete
    // would be a guess that is sometimes plainly false.
    if (all.length >= EXHAUSTIVE_MAX_ROWS || pages >= EXHAUSTIVE_MAX_PAGES) {
      return {
        data: null,
        error: {
          message:
            `Reading this table was stopped after ${all.length.toLocaleString('en-US')} rows ` +
            `over ${pages} requests, at the safety limits of ` +
            `${EXHAUSTIVE_MAX_ROWS.toLocaleString('en-US')} rows and ` +
            `${EXHAUSTIVE_MAX_PAGES} requests. Whether more rows remain was never ` +
            `checked, so this result cannot be treated as complete.`,
          code: 'EXHAUSTIVE_READ_LIMIT',
        },
      }
    }
  }
}
